/**
 * El calendario de campañas en días y en palabras (fase 3 del plan de campañas, 29/10/2026): la vista (mes o semana) y su
 * rango, en qué días sale cada campaña, la regla de fatiga de la marca y las advertencias. Puro y testeado
 * (`calendario.test.ts`). Los días son los del navegador, como las fechas de la lista (`fechaHora`).
 *
 * La fatiga no cruza personas: con lo que se sabe de cada campaña (sus días, su público, por dónde sale y si la fatiga la
 * corta) alcanza para avisar que dos salen demasiado cerca. Quién queda afuera lo cuenta cada campaña al confirmarse.
 */

import { MESES, num, plural } from "../../campana-nueva/utils/format";
import { CANALES, R_CAMPANAS } from "./campanas";
import type { CampanaEnCalendario, Canal } from "../types";

export const R_CALENDARIO = `${R_CAMPANAS}/calendario`;

export type Vista = "mes" | "semana";
export const VISTAS: Vista[] = ["mes", "semana"];
export const VISTA: Record<Vista, { texto: string; anterior: string; siguiente: string; vacio: string }> = {
  mes: { texto: "Mes", anterior: "Mes anterior", siguiente: "Mes siguiente", vacio: "Nada programado ni enviado en este mes." },
  semana: { texto: "Semana", anterior: "Semana anterior", siguiente: "Semana siguiente", vacio: "Nada programado ni enviado en esta semana." },
};

/** La semana empieza el lunes. */
export const DIAS_SEMANA = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];
const DIAS_LARGOS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const DIA_MS = 24 * 60 * 60 * 1000;
/** Un tramo de envío no se recorre más allá de un año: la vista más larga son 42 días. */
const DIAS_MAX = 366;

/* ── Los días ─────────────────────────────────────────────────────────────────────────────── */

const dos = (n: number) => String(n).padStart(2, "0");

/** «2026-10-29»: el día de una fecha, en la hora del navegador. */
export const claveDia = (d: Date) => `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;

/** La medianoche de un día («2026-10-29»); null si no es un día («2026-02-30», «ayer»). */
export function deClave(s: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s ?? ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return claveDia(d) === s ? d : null;
}

export const sumarDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export const lunesDe = (d: Date) => sumarDias(d, -((d.getDay() + 6) % 7));

/** Cuántos días van de `a` a `b` (dos claves), contados en el calendario: el cambio de hora no corre un día. */
export function diasEntre(a: string, b: string): number {
  const x = deClave(a);
  const y = deClave(b);
  if (!x || !y) return NaN;
  return Math.round((Date.UTC(y.getFullYear(), y.getMonth(), y.getDate()) - Date.UTC(x.getFullYear(), x.getMonth(), x.getDate())) / DIA_MS);
}

const SOLO_DIA = /^\d{4}-\d{2}-\d{2}$/;

/** El día de un instante ISO o de un día sin hora («2026-09-24», los planes viejos: leído como instante, es el día anterior). */
export function diaDe(s: string | null | undefined): string | null {
  if (!s) return null;
  if (SOLO_DIA.test(s)) return deClave(s) ? s : null;
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? null : claveDia(t);
}

/** «19:00» de un instante; null para un día sin hora. */
export function horaDe(s: string | null | undefined): string | null {
  if (!s || SOLO_DIA.test(s)) return null;
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? null : `${dos(t.getHours())}:${dos(t.getMinutes())}`;
}

/* ── La vista y la URL ────────────────────────────────────────────────────────────────────── */

export interface EstadoVista {
  vista: Vista;
  /** el día que se mira («2026-10-29»): su mes o su semana */
  dia: string;
  /** vacío = los dos canales */
  canal?: Canal;
}

/** Lo que trae la URL (`?vista=semana&dia=2026-10-29&canal=whatsapp`). Lo que no se conoce se ignora: sin día, hoy. */
export function vistaDeLaUrl(sp: URLSearchParams, hoy: Date): EstadoVista {
  const dia = sp.get("dia");
  const canal = sp.get("canal");
  return {
    vista: sp.get("vista") === "semana" ? "semana" : "mes",
    dia: dia && deClave(dia) ? dia : claveDia(hoy),
    canal: CANALES.find((c) => c === canal),
  };
}

/** La URL de una vista: sin el mes (es la de siempre), sin hoy y sin el canal si son los dos. */
export function urlDeLaVista(v: EstadoVista, hoy: Date): URLSearchParams {
  const sp = new URLSearchParams();
  if (v.vista !== "mes") sp.set("vista", v.vista);
  if (v.dia !== claveDia(hoy)) sp.set("dia", v.dia);
  if (v.canal) sp.set("canal", v.canal);
  return sp;
}

export interface RangoVista {
  /** la medianoche del primer día */
  desde: Date;
  /** la medianoche del día que sigue al último */
  hasta: Date;
  /** sus días, en semanas enteras de lunes a domingo */
  dias: string[];
}

/** El mes (con las semanas de las puntas enteras: 35 o 42 días) o la semana del día que se mira. */
export function rangoDeVista(vista: Vista, dia: string): RangoVista {
  const d = deClave(dia) ?? new Date();
  const desde = vista === "mes" ? lunesDe(new Date(d.getFullYear(), d.getMonth(), 1)) : lunesDe(d);
  const hasta = vista === "mes" ? sumarDias(lunesDe(new Date(d.getFullYear(), d.getMonth() + 1, 0)), 7) : sumarDias(desde, 7);
  const dias: string[] = [];
  for (let x = desde; x < hasta; x = sumarDias(x, 1)) dias.push(claveDia(x));
  return { desde, hasta, dias };
}

/** El día que se mira después de una flecha: el día 1 del mes de al lado, o el mismo día de la semana de al lado. */
export function moverVista(vista: Vista, dia: string, paso: 1 | -1): string {
  const d = deClave(dia) ?? new Date();
  return claveDia(vista === "mes" ? new Date(d.getFullYear(), d.getMonth() + paso, 1) : sumarDias(d, 7 * paso));
}

/** «Octubre de 2026»; la semana, «26 oct – 1 nov 2026» (con el año en las dos puntas si cambia en el medio). */
export function tituloDeVista(vista: Vista, dia: string): string {
  const d = deClave(dia) ?? new Date();
  if (vista === "mes") {
    const mes = MESES[d.getMonth()];
    return `${mes[0].toUpperCase()}${mes.slice(1)} de ${d.getFullYear()}`;
  }
  const a = lunesDe(d);
  const b = sumarDias(a, 6);
  const corto = (x: Date, anio: boolean) => `${x.getDate()} ${MESES_CORTOS[x.getMonth()]}${anio ? ` ${x.getFullYear()}` : ""}`;
  if (a.getFullYear() !== b.getFullYear()) return `${corto(a, true)} – ${corto(b, true)}`;
  if (a.getMonth() === b.getMonth()) return `${a.getDate()} – ${corto(b, true)}`;
  return `${corto(a, false)} – ${corto(b, true)}`;
}

/** «jueves 29 de octubre». */
export function tituloDelDia(dia: string): string {
  const d = deClave(dia);
  return d ? `${DIAS_LARGOS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]}` : dia;
}

/* ── Cada campaña en sus días ─────────────────────────────────────────────────────────────── */

/** Un día en que sale una campaña: cuántos salen ese día (null si no se sabe por día) y a qué hora sale el primero. */
export interface DiaDeEnvio {
  dia: string;
  cantidad: number | null;
  hora: string | null;
}

/** Lo que va en el calendario: una campaña en sus días, o la próxima repetición de una (que todavía no es una campaña). */
export interface Envio {
  clave: string;
  campana: CampanaEnCalendario;
  proxima: boolean;
  dias: DiaDeEnvio[];
}

/**
 * En qué días sale una campaña:
 * - programada: los días de su plan (la del oficial que sale en tandas) o el día en que sale;
 * - enviando: de cuando empezó hasta hoy, y los días de su plan que faltan;
 * - las demás: de cuando empezó a cuando terminó; sin fin (una por QR, una con segmentación, una en pausa), el día en que
 *   empezó.
 * Cuántos salen cada día lo dice su plan; si sale en un solo día, son todas sus personas.
 */
export function diasDeLaCampana(c: CampanaEnCalendario, hoy: Date): DiaDeEnvio[] {
  const inicio = diaDe(c.inicio);
  if (!inicio) return [];
  const plan = new Map<string, DiaDeEnvio>();
  for (const t of c.tandas) {
    const dia = diaDe(t.fecha);
    if (!dia) continue;
    const antes = plan.get(dia);
    plan.set(dia, { dia, cantidad: (antes?.cantidad ?? 0) + t.cantidad, hora: antes ? antes.hora : horaDe(t.fecha) });
  }
  const deA = (hasta: string): DiaDeEnvio[] => {
    const primero = deClave(inicio) as Date;
    const n = Math.min(DIAS_MAX, Math.max(0, diasEntre(inicio, hasta)));
    return Array.from({ length: n + 1 }, (_, k) => {
      const dia = claveDia(sumarDias(primero, k));
      return plan.get(dia) ?? { dia, cantidad: null, hora: k === 0 ? horaDe(c.inicio) : null };
    });
  };
  let dias: DiaDeEnvio[];
  if (c.estado === "programada") dias = plan.size ? [...plan.values()] : deA(inicio);
  else if (c.estado === "enviando") {
    const hoyDia = claveDia(hoy);
    const hastaHoy = hoyDia > inicio ? hoyDia : inicio;
    dias = [...deA(hastaHoy), ...[...plan.values()].filter((d) => d.dia > hastaHoy)];
  } else dias = deA(diaDe(c.fin) ?? inicio);
  dias.sort((a, b) => a.dia.localeCompare(b.dia));
  return dias.length === 1 && dias[0].cantidad === null ? [{ ...dias[0], cantidad: c.personas }] : dias;
}

const porOrden = (a: Envio, b: Envio) =>
  a.dias[0].dia.localeCompare(b.dias[0].dia) ||
  (a.dias[0].hora ?? "").localeCompare(b.dias[0].hora ?? "") ||
  a.campana.nombre.localeCompare(b.campana.nombre, "es") ||
  a.clave.localeCompare(b.clave);

/** Las campañas en sus días, más la próxima repetición de las que se repiten: de la primera que sale a la última. */
export function enviosDelCalendario(cs: readonly CampanaEnCalendario[], hoy: Date): Envio[] {
  const out: Envio[] = [];
  for (const c of cs) {
    const dias = diasDeLaCampana(c, hoy);
    if (dias.length) out.push({ clave: c.clave, campana: c, proxima: false, dias });
    const proxima = diaDe(c.proximaRepeticion);
    if (proxima) out.push({ clave: `${c.clave}:proxima`, campana: c, proxima: true, dias: [{ dia: proxima, cantidad: null, hora: horaDe(c.proximaRepeticion) }] });
  }
  return out.sort(porOrden);
}

/** Una campaña en uno de sus días: cuál es (`k` de `n`). */
export interface EnvioDelDia {
  envio: Envio;
  dia: DiaDeEnvio;
  k: number;
  n: number;
}

/** Lo que sale cada día de la vista, con el filtro de canal: de la primera hora a la última (sin hora, al final). */
export function porDia(envios: readonly Envio[], dias: readonly string[], canal?: Canal): Map<string, EnvioDelDia[]> {
  const out = new Map<string, EnvioDelDia[]>(dias.map((d) => [d, []]));
  for (const e of envios) {
    if (canal && !e.campana.canales.includes(canal)) continue;
    e.dias.forEach((dia, i) => out.get(dia.dia)?.push({ envio: e, dia, k: i + 1, n: e.dias.length }));
  }
  for (const xs of out.values()) xs.sort((a, b) => (a.dia.hora ?? "~").localeCompare(b.dia.hora ?? "~") || porOrden(a.envio, b.envio));
  return out;
}

/** «600 personas» ese día; si no se sabe por día, «día 2 de 3»; si no se sabe, nada. */
export function textoDelDia(x: EnvioDelDia): string | null {
  if (x.dia.cantidad !== null) return `${num(x.dia.cantidad)} ${plural(x.dia.cantidad, "persona", "personas")}`;
  return x.n > 1 ? `día ${x.k} de ${x.n}` : null;
}

/* ── La fatiga ────────────────────────────────────────────────────────────────────────────── */

/** La regla de la marca en una línea («Tu marca: 1 mensaje cada 7 días por persona», o «como mucho 2») y lo que hace. */
export function reglaDeFatiga(dias: number, maximo = 1): { titulo: string; detalle: string } {
  if (!(dias > 0)) return { titulo: "Tu marca no tiene regla de fatiga", detalle: "Por el WhatsApp oficial, a una persona le pueden llegar varias promociones seguidas." };
  const ventana = dias === 1 ? "el último día" : `los últimos ${num(dias)} días`;
  const k = Math.max(1, Math.floor(maximo) || 1);
  return {
    titulo: `Tu marca: ${k === 1 ? "1 mensaje" : `como mucho ${num(k)} mensajes`} ${dias === 1 ? "por día" : `cada ${num(dias)} días`} por persona`,
    detalle: k === 1
      ? `Por el WhatsApp oficial, a quien recibió una promoción en ${ventana} (también por tus líneas de QR) no le sale otra: queda afuera de la campaña.`
      : `Por el WhatsApp oficial, a quien ya recibió ${num(k)} en ${ventana} (también por tus líneas de QR) no le sale otra. Con más de uno por persona, el calendario no marca las que salen cerca.`,
  };
}

export interface Advertencia {
  clave: string;
  /** las dos, en el orden en que salen */
  a: Envio;
  b: Envio;
  /** los días entre el último de la primera y el primero de la segunda (0: salen los mismos días) */
  dias: number;
  mismoPublico: boolean;
  texto: string;
}

/** Sus envíos cuentan para la fatiga de otra: los WhatsApp de las líneas, y los del oficial si son de marketing (como el motor). */
const cuenta = (e: Envio) => e.campana.mundo !== "oficial" || e.campana.fatiga;
/** A `x` la fatiga le saca a quien recibió `y`. */
const corta = (x: Envio, y: Envio) => x.campana.fatiga && cuenta(y);
const nombre = (e: Envio) => `«${e.campana.nombre || "Sin nombre"}»${e.proxima ? " (la próxima repetición)" : ""}`;

/**
 * Entre éstas no se avisa: son el mismo envío o la fatiga no corre entre ellas (como el motor, `campanasQueNoFatigan`).
 * La campaña y su próxima repetición, las de una misma serie de repeticiones, un seguimiento y su original, y una campaña
 * con segmentación y sus envíos (su WhatsApp oficial y su Snov).
 */
export function sinFatigaEntre(x: CampanaEnCalendario, y: CampanaEnCalendario): boolean {
  if (x.clave === y.clave) return true;
  if (x.mundo === "oficial" && y.mundo === "oficial") {
    const serie = (c: CampanaEnCalendario) => c.repeticionDe?.id ?? c.id;
    if (serie(x) === serie(y) || x.seguimientoDe?.id === y.id || y.seguimientoDe?.id === x.id) return true;
  }
  const esEnvioDe = (c: CampanaEnCalendario, s: CampanaEnCalendario) => s.mundo === "segmentacion" && c.parteDe?.id === s.id;
  return esEnvioDe(x, y) || esEnvioDe(y, x) || (!!x.parteDe && x.parteDe.id === y.parteDe?.id);
}

function textoAdvertencia(a: Envio, b: Envio, dias: number, mismoPublico: boolean): string {
  const cuando = dias === 0 ? "salen los mismos días" : `salen con ${num(dias)} ${plural(dias, "día", "días")} de diferencia`;
  let efecto: string;
  if (dias > 0) efecto = corta(b, a) ? `la fatiga va a dejar afuera de ${nombre(b)} a quien reciba ${nombre(a)}` : `a quien esté en las dos le pueden llegar dos mensajes en ${num(dias)} ${plural(dias, "día", "días")}`;
  else if (corta(a, b) && corta(b, a)) efecto = "la fatiga va a dejar afuera de una a quien ya haya recibido la otra";
  else if (corta(b, a)) efecto = `la fatiga va a dejar afuera de ${nombre(b)} a quien ya haya recibido ${nombre(a)}`;
  else if (corta(a, b)) efecto = `la fatiga va a dejar afuera de ${nombre(a)} a quien ya haya recibido ${nombre(b)}`;
  else efecto = "a quien esté en las dos le pueden llegar dos mensajes los mismos días";
  return `${nombre(a)} y ${nombre(b)} ${cuando}${mismoPublico ? ", al mismo público" : ""}: ${efecto}.`;
}

/**
 * Las campañas de WhatsApp que salen más cerca que la regla de fatiga: cada una con la anterior más cercana (la que le
 * saca más gente), sin las simuladas (no fatigan a nadie de verdad) ni las que la fatiga no cruza (`sinFatigaEntre`). Sólo
 * los pares con algún día en `visibles`. Sin regla (0 días), ninguna.
 */
export function advertenciasDeFatiga(envios: readonly Envio[], fatigaDias: number, visibles: ReadonlySet<string>): Advertencia[] {
  if (!(fatigaDias > 0)) return [];
  const wa = envios.filter((e) => e.campana.canales.includes("whatsapp") && !e.campana.simulada).sort(porOrden);
  const out: Advertencia[] = [];
  wa.forEach((b, j) => {
    let cerca: { a: Envio; dias: number } | null = null;
    for (const a of wa.slice(0, j)) {
      if (sinFatigaEntre(a.campana, b.campana)) continue;
      const dias = Math.max(0, diasEntre(a.dias[a.dias.length - 1].dia, b.dias[0].dia));
      // a la misma distancia, la que sale más cerca de ella (la última de la lista)
      if (!cerca || dias <= cerca.dias) cerca = { a, dias };
    }
    if (!cerca || cerca.dias >= fatigaDias) return;
    const { a, dias } = cerca;
    if (![...a.dias, ...b.dias].some((d) => visibles.has(d.dia))) return;
    const mismoPublico = !!a.campana.audiencia && a.campana.audiencia === b.campana.audiencia;
    out.push({ clave: `${a.clave}|${b.clave}`, a, b, dias, mismoPublico, texto: textoAdvertencia(a, b, dias, mismoPublico) });
  });
  return out;
}
